/**
 * Wunschlisten-Funktionalität
 * Speichert Produkte in localStorage und synchronisiert mit Shopify Customer Account API (optional)
 */

class WishlistManager {
  constructor() {
    this.storageKey = 'henne_wishlist';
    this.wishlist = this.loadWishlist();
    this.init();
  }

  /**
   * Initialisiert die Wunschlisten-Funktionalität
   */
  init() {
    // Event-Listener für alle Wunschlisten-Buttons
    document.addEventListener('click', (e) => {
      const button = e.target.closest('.wishlist-button');
      if (button) {
        e.preventDefault();
        e.stopPropagation();
        this.toggleProduct(button);
      }
    });

    // Aktualisiere Button-States beim Laden der Seite
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        this.updateAllButtons();
        this.updateWishlistCount();
      });
    } else {
      this.updateAllButtons();
      this.updateWishlistCount();
    }

    // Event-Listener für Wunschlisten-Updates
    document.addEventListener('wishlistUpdated', () => {
      this.updateAllButtons();
      this.updateWishlistCount();
    });
  }

  /**
   * Lädt die Wunschliste aus localStorage
   */
  loadWishlist() {
    try {
      const stored = localStorage.getItem(this.storageKey);
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.error('Fehler beim Laden der Wunschliste:', error);
      return [];
    }
  }

  /**
   * Speichert die Wunschliste in localStorage
   */
  saveWishlist() {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(this.wishlist));
      document.dispatchEvent(new CustomEvent('wishlistUpdated'));
    } catch (error) {
      console.error('Fehler beim Speichern der Wunschliste:', error);
    }
  }

  /**
   * Prüft, ob ein Produkt in der Wunschliste ist
   */
  isInWishlist(productId) {
    return this.wishlist.some(item => item.id === productId.toString());
  }

  /**
   * Fügt ein Produkt zur Wunschliste hinzu
   */
  addProduct(button) {
    const productData = {
      id: button.dataset.productId,
      handle: button.dataset.productHandle,
      title: button.dataset.productTitle,
      url: button.dataset.productUrl,
      image: button.dataset.productImage,
      price: button.dataset.productPrice,
      compareAtPrice: button.dataset.productCompareAtPrice || null,
      addedAt: new Date().toISOString()
    };

    if (!this.isInWishlist(productData.id)) {
      this.wishlist.push(productData);
      this.saveWishlist();
      this.showNotification('Zu Wunschliste hinzugefügt', 'success');
      return true;
    }
    return false;
  }

  /**
   * Entfernt ein Produkt aus der Wunschliste
   */
  removeProduct(productId) {
    const index = this.wishlist.findIndex(item => item.id === productId.toString());
    if (index > -1) {
      this.wishlist.splice(index, 1);
      this.saveWishlist();
      this.showNotification('Von Wunschliste entfernt', 'info');
      return true;
    }
    return false;
  }

  /**
   * Togglet ein Produkt in der Wunschliste
   */
  toggleProduct(button) {
    const productId = button.dataset.productId;
    
    if (this.isInWishlist(productId)) {
      this.removeProduct(productId);
      this.updateButton(button, false);
    } else {
      this.addProduct(button);
      this.updateButton(button, true);
    }
  }

  /**
   * Aktualisiert den State eines einzelnen Buttons
   */
  updateButton(button, isInWishlist) {
    const emptyIcon = button.querySelector('.wishlist-icon--empty');
    const filledIcon = button.querySelector('.wishlist-icon--filled');
    
    if (isInWishlist) {
      button.classList.add('wishlist-button--active');
      button.setAttribute('aria-label', 'Von Wunschliste entfernen');
      button.setAttribute('title', 'Von Wunschliste entfernen');
      if (emptyIcon) emptyIcon.style.display = 'none';
      if (filledIcon) filledIcon.style.display = 'block';
    } else {
      button.classList.remove('wishlist-button--active');
      button.setAttribute('aria-label', 'Zu Wunschliste hinzufügen');
      button.setAttribute('title', 'Zu Wunschliste hinzufügen');
      if (emptyIcon) emptyIcon.style.display = 'block';
      if (filledIcon) filledIcon.style.display = 'none';
    }
  }

  /**
   * Aktualisiert alle Wunschlisten-Buttons auf der Seite
   */
  updateAllButtons() {
    const buttons = document.querySelectorAll('.wishlist-button');
    buttons.forEach(button => {
      const productId = button.dataset.productId;
      this.updateButton(button, this.isInWishlist(productId));
    });
  }

  /**
   * Aktualisiert die Wunschlisten-Anzahl im Header
   */
  updateWishlistCount() {
    const countElements = document.querySelectorAll('.wishlist-count');
    const count = this.wishlist.length;
    
    countElements.forEach(element => {
      element.textContent = count;
      element.style.display = count > 0 ? 'inline-block' : 'none';
    });
  }

  /**
   * Gibt alle Produkte in der Wunschliste zurück
   */
  getWishlist() {
    return this.wishlist;
  }

  /**
   * Leert die Wunschliste
   */
  clearWishlist() {
    this.wishlist = [];
    this.saveWishlist();
    this.updateAllButtons();
    this.updateWishlistCount();
  }

  /**
   * Zeigt eine Benachrichtigung
   */
  showNotification(message, type = 'info') {
    // Erstelle Notification-Element
    const notification = document.createElement('div');
    notification.className = `wishlist-notification wishlist-notification--${type}`;
    notification.textContent = message;
    
    document.body.appendChild(notification);
    
    // Trigger Animation
    setTimeout(() => {
      notification.classList.add('wishlist-notification--show');
    }, 10);
    
    // Entferne nach 3 Sekunden
    setTimeout(() => {
      notification.classList.remove('wishlist-notification--show');
      setTimeout(() => {
        notification.remove();
      }, 300);
    }, 3000);
  }
}

// Initialisiere Wunschlisten-Manager
const wishlistManager = new WishlistManager();

// Export für globale Verwendung
window.WishlistManager = wishlistManager;

